/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { CloseOutlined } from '@ant-design/icons'
import { Modal } from 'antd'
import React, { useCallback, useEffect, useMemo } from 'react'
import Text from 'src/components/ts-components/Text'
import Button from 'src/components/ts-components/Button'
import { TaxProvider, TaxData, useTaxContext } from './TaxContext'
import TaxModalContent from './TaxModalContent'
import useWindowSize from 'src/hooks/useWindowSize'
import { useModalScroll } from 'src/hooks/useModalScroll'

const modalStyles = {
  header: {
    backgroundColor: colors.SECONDARY_300,
    paddingLeft: '16px',
    paddingRight: '16px',
  },
  body: {
    borderBottom: `1px solid ${colors.SOFT_STEEL}`,
    borderTop: `1px solid ${colors.SOFT_STEEL}`,
    backgroundColor: colors.SECONDARY_300,
  },
  content: {
    backgroundColor: colors.SECONDARY_300,
    paddingTop: '8px',
    paddingBottom: '16px',
    paddingLeft: '0',
    paddingRight: '0',
  },
  wrapper: {
    zIndex: 10001,
  },
  mask: {
    backgroundColor: 'rgba(6, 10, 16, 0.8)',
    zIndex: 10000,
  },
  footer: {
    paddingLeft: '16px',
    paddingRight: '16px',
  },
}

type TaxModalProps = {
  isOpen: boolean
  onClose: () => void
  initialData?: TaxData
  accountUid: string
  onSuccess: () => void
  defaultCountryId?: string
}

// Inner component that has access to the context for the footer
const TaxModalInner: React.FC<{
  isOpen: boolean
  onClose: () => void
  isMobile: boolean
}> = ({ isOpen, onClose, isMobile }) => {
  const { state, isEdit, onSubmit, onCancel } = useTaxContext()

  const closeIcon = useCallback(() => {
    return (
      <CloseOutlined
        css={css`
          color: ${colors.SOLITUDE};
          font-size: 16px;
        `}
      />
    )
  }, [])

  const getHeader = useCallback(() => {
    return (
      <Text variant='heading3' weight='semiBold' color={colors.WHITE}>
        {isEdit ? 'Update Tax' : 'Add Tax'}
      </Text>
    )
  }, [isEdit])

  const getFooter = useCallback(() => {
    return (
      <div
        css={css`
          display: flex;
          justify-content: flex-end;
          gap: 16px;
          width: 50%;
          margin-left: auto;
          ${isMobile &&
          css`
            width: 100%;
            flex-direction: column-reverse;
          `}
        `}
      >
        <Button
          text='Cancel'
          onClick={onCancel}
          variant='secondary'
          disabled={state.isLoading}
        />
        <Button
          text={isEdit ? 'Update' : 'Confirm'}
          onClick={onSubmit}
          variant='primary'
          isLoading={state.isLoading}
          disabled={state.isLoading}
        />
      </div>
    )
  }, [isMobile, state.isLoading, isEdit, onSubmit, onCancel])

  return (
    <Modal
      css={css`
        display: flex;
        flex: 1;
        height: ${isMobile ? '100%' : 'auto'} !important;
        max-height: ${isMobile ? '100%' : '90vh'};
        margin: ${isMobile ? '0' : '24px auto'};
        padding: 0;

        .ant-modal-content {
          height: ${isMobile ? '100%' : 'auto'} !important;
          max-height: ${isMobile ? '100%' : '90vh'};
          border-radius: ${isMobile ? '0' : '8px'};
          display: flex;
          flex-direction: column;
        }
        .ant-modal-body {
          flex: 1;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
        }
      `}
      width={isMobile ? '100%' : 600}
      closeIcon={closeIcon()}
      title={getHeader()}
      footer={getFooter()}
      open={isOpen}
      onCancel={onClose}
      centered={!isMobile}
      styles={modalStyles}
      maskClosable={false}
    >
      <TaxModalContent />
    </Modal>
  )
}

const TaxModal: React.FC<TaxModalProps> = ({
  isOpen,
  onClose,
  initialData,
  accountUid,
  onSuccess,
  defaultCountryId,
}) => {
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width <= 768, [width])
  const { openModal, closeModal } = useModalScroll()

  useEffect(() => {
    if (isOpen) {
      openModal()
    } else {
      closeModal()
    }
  }, [closeModal, isOpen, openModal])

  if (!isOpen) {
    return null
  }

  return (
    <TaxProvider
      isOpen={isOpen}
      onClose={onClose}
      initialData={initialData}
      accountUid={accountUid}
      onSuccess={onSuccess}
      defaultCountryId={defaultCountryId}
    >
      <TaxModalInner isOpen={isOpen} onClose={onClose} isMobile={isMobile} />
    </TaxProvider>
  )
}

export default TaxModal
